import classNames         from 'classnames';
import * as React         from 'react';
import { createPortal }   from 'react-dom';
import { useTranslation } from 'react-i18next';

import './zoom-preview.style.scss';

import type { ZoomPreviewProps } from './zoom-preview.props';

const ZoomPreview: React.FC<ZoomPreviewProps> = function(props) {
  const { t } = useTranslation();
  const {
          alt,
          backSideImage,
          backSideImageAlt,
          children,
          enabled,
          frontSideImage,
          frontSideImageAlt,
          interiorImage,
          interiorImageAlt,
          originalRef,
        }     = props;

  const last   = React.useRef({
    x: 0,
    y: 0,
  });
  const lastDX = React.useRef(0);
  const start  = React.useRef({
    x: 0,
    y: 0,
  });
  const zoomed = React.useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen]           = React.useState(false);
  const [height, setHeight]           = React.useState(0);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [scrollTop, setScrollTop]     = React.useState(0);
  const [side, setSide]               = React.useState<'inside' | 'outside'>('outside');
  const [transform, setTransform]     = React.useState<React.CSSProperties['transform']>('translate(-50%, -50%)');

  const isMobile = React.useCallback(() => {
    if (isSSR()) {
      return false;
    }

    return document.documentElement.clientWidth <= 768;
  }, []);

  const calculatedHeight = React.useMemo(() => {
    if (isMobile()) {
      return document.documentElement.clientHeight * 2 - 60;
    }

    return height;
  }, [height]);
  const images           = React.useMemo(() => {
    if (!isOpen || !originalRef.current) {
      return [];
    }

    return Array.prototype.slice
      .call(originalRef.current.querySelectorAll('picture'))
      .map((picture) => {
        return picture.querySelector('img');
      });
  }, [isOpen, originalRef]);
  const width            = React.useMemo(() => {
    return isMobile()
      ? document.documentElement.clientWidth * 2 - 60
      : 744 * 2.05714286 + 60;
  }, []);

  const getYOffset     = React.useCallback(() => {
    return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
  }, []);
  const handleCloseEnd = React.useCallback(() => {
    zoomed.current?.classList.remove('zoom-image--opened');
    zoomed.current?.removeEventListener('transitionend', handleCloseEnd);

    setIsAnimating(false);
    setIsOpen(false);
  }, []);
  const close          = React.useCallback(() => {
    if (isAnimating || !originalRef.current) {
      return;
    }

    setIsAnimating(true);

    originalRef.current.classList.remove('zoom-image--hidden');

    document.body.classList.remove('zoom--opened');

    if (zoomed.current) {
      zoomed.current?.addEventListener('transitionend', handleCloseEnd);
    } else {
      handleCloseEnd();
    }
  }, [handleCloseEnd, isAnimating, originalRef]);
  const handleKeyUp    = React.useCallback((event: KeyboardEvent) => {
    if (!isAnimating && originalRef.current && event.key === 'Escape') {
      close();
    }
  }, [close, isAnimating, originalRef]);
  const handleClick    = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isAnimating && originalRef.current) {
      close();
    }
  }, [close, isAnimating, originalRef]);
  const handleOpenEnd  = React.useCallback(() => {
    zoomed.current?.removeEventListener('transitionend', handleOpenEnd);
  }, []);
  const handleScroll   = React.useCallback(() => {
    if (Math.abs(scrollTop - getYOffset()) > 40) {
      close();
    }
  }, [close, isAnimating, originalRef, scrollTop]);
  const touchMove      = React.useCallback((event: TouchEvent) => {
    const [touch] = event.touches;

    lastDX.current = last.current.x - touch.clientX;
    last.current.x = touch.clientX;
    last.current.y = touch.clientY;
  }, []);
  const touchEnd       = React.useCallback((event: TouchEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const diffX     = last.current.x - start.current.x;
    let side: 'inside' | 'outside' | undefined;
    const threshold = window.innerWidth / 20;

    if (Math.abs(diffX) > window.innerWidth / 5 && (diffX * lastDX.current) <= 0) {
      if (diffX > window.innerWidth / 5 && lastDX.current <= 0) {
        side = 'outside';
      } else if (diffX < -window.innerWidth / 5 && lastDX.current >= 0) {
        side = 'inside';
      }
    } else if (Math.abs(lastDX.current) > threshold) {
      if (lastDX.current < -threshold) {
        side = 'outside';
      } else if (lastDX.current > threshold) {
        side = 'inside';
      }
    }

    if (typeof side !== 'undefined') {
      setSide(side);

      document.removeEventListener('touchmove', touchMove);
      document.removeEventListener('touchend', touchEnd);
    } else if (Math.abs(start.current.y - last.current.y) <= window.innerHeight / 20) {
      close();
    }
  }, [close, touchMove]);
  const touchStart     = React.useCallback((event: TouchEvent) => {
    const [touch] = event.touches;

    last.current.x = start.current.x = touch.clientX;
    last.current.y = start.current.y = touch.clientY;

    document.addEventListener('touchmove', touchMove);
    document.addEventListener('touchend', touchEnd);
  }, [touchEnd, touchMove]);

  React.useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', handleScroll);
      document.addEventListener('keyup', handleKeyUp);
      document.addEventListener('touchstart', touchStart);
      window.addEventListener('resize', close);

      setHeight((prev) => {
        return images.reduce((acc, cur) => {
          return Math.max(acc, 744 / cur.naturalWidth * cur.naturalHeight);
        }, prev);
      });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('touchstart', touchStart);
      document.removeEventListener('touchmove', touchMove);
      document.removeEventListener('touchend', touchEnd);
      window.removeEventListener('resize', close);
    };
  }, [isOpen]);
  React.useEffect(() => {
    if (isOpen && images.length > 0) {
      setScrollTop(getYOffset());

      requestAnimationFrame(() => {
        document.body.classList.toggle('zoom--opened', true);
      });

      originalRef.current?.classList.toggle('zoom-image--hidden', true);

      zoomed.current?.classList.toggle('zoom-image--opened', true);
      zoomed.current?.addEventListener('transitionend', handleOpenEnd);

      const viewportWidth  = document.documentElement.clientWidth - 15 * 2;
      const viewportHeight = document.documentElement.clientHeight - 15 * 2;
      const scaleX         = viewportWidth / width;
      const scaleY         = viewportHeight / calculatedHeight;
      const scale          = Math.min(scaleX, scaleY);

      setTransform(`scale(${scale}) translate(-50%, -50%)`);
    }

    return () => {
      zoomed.current?.removeEventListener('transitionend', handleOpenEnd);
    };
  }, [calculatedHeight, images, isOpen, width]);

  const preview = React.Children.only<any>(children);

  return (
    <>
      {React.cloneElement(preview, {
        ...preview.props,
        className: classNames(preview.props.className, {
          'zoom-image': enabled,
        }),
        onClick() {
          if (enabled) {
            setIsOpen(true);
          }
        },
      })}
      {isOpen && !!originalRef.current && createPortal((
        <div
          className={classNames('zoom-overlay', { mobile: isMobile() })}
          onClick={handleClick}
          ref={zoomed}
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.75)' }}
        >
          <i className={'ic-close'} />
          <div
            className={classNames('zoom', {
              mobile: isMobile(),
              [side]: typeof interiorImage !== 'string',
            })}
            style={{
              height: `${calculatedHeight}px`,
              transform,
              width:  `${width}px`,
            }}
          >
            {typeof interiorImage === 'string'
              ? (
                <picture>
                  <source
                    srcSet={`${interiorImage}.webp?w=744&default=vertical&t=${BUILD_TIME}`}
                    type={'image/webp'}
                  />
                  <img
                    alt={interiorImageAlt ?? `${alt} - ${t('frontSideImage')} - original`}
                    onContextMenu={(event) => {
                      event.preventDefault();
                    }}
                    src={`${interiorImage}.png?w=744&default=vertical&t=${BUILD_TIME}`}
                  />
                </picture>
              )
              : (
                <>
                  <picture>
                    <source
                      srcSet={`${frontSideImage}.webp?w=744&default=vertical&t=${BUILD_TIME}`}
                      type={'image/webp'}
                    />
                    <img
                      alt={frontSideImageAlt ?? `${alt} - ${t('frontSideImage')} - original`}
                      onContextMenu={(event) => {
                        event.preventDefault();
                      }}
                      src={`${frontSideImage}.png?w=744&default=vertical&t=${BUILD_TIME}`}
                    />
                  </picture>
                  <picture>
                    <source
                      srcSet={`${backSideImage}.webp?w=744&default=vertical&t=${BUILD_TIME}`}
                      type={'image/webp'}
                    />
                    <img
                      alt={backSideImageAlt ?? `${alt} - ${t('backSideImage')} - original`}
                      onContextMenu={(event) => {
                        event.preventDefault();
                      }}
                      src={`${backSideImage}.png?w=744&default=vertical&t=${BUILD_TIME}`}
                    />
                  </picture>
                </>
              )
            }
          </div>
        </div>
      ), document.body)}
    </>
  );
};

export default ZoomPreview;